/** Разбор ответа ассистента на текст и графики.
 *
 *  ПОЧЕМУ МОДЕЛЬ ПРИСЫЛАЕТ ДАННЫЕ, А НЕ HTML. Разметку от модели пришлось бы
 *  вставлять как разметку, а в ответе живут описания трат — текст, который ввёл
 *  сам человек. Запись вида `<img onerror=…>`, пересказанная моделью внутрь
 *  HTML, выполнилась бы в мини-аппе, где лежит подпись Telegram. Здесь модель
 *  разметки не пишет вовсе, и вставлять нечего: приходят подписи и числа,
 *  рисует их приложение.
 *
 *  ПОЧЕМУ БЛОК, А НЕ ОТДЕЛЬНОЕ ПОЛЕ ОТВЕТА. Ответ печатается потоком, и второе
 *  поле пришлось бы протаскивать через события потока. Огороженный блок внутри
 *  того же текста доезжает сам, а недописанный просто не показывается.
 */

/** Один график. Вид ровно один — горизонтальные полоски: ими одинаково хорошо
 *  читаются «по категориям», «по месяцам» и «сравни два периода». Второй вид
 *  добавим, когда найдётся вопрос, на который полоски отвечают плохо. */
export type Chart = {
  title: string;
  /** Подпись и величина. Величина — СТРОКА: деньги числом не возим (MA-INV-6). */
  rows: [string, string][];
};

export type Block =
  | { kind: "text"; text: string }
  | { kind: "chart"; chart: Chart };

const FENCE = "```chart";

/** Максимум полосок. Двадцать — уже не картинка, а таблица, и на телефоне
 *  такая не читается. */
const MAX_ROWS = 8;

/**
 * Разбить ответ на куски. Неполный блок в конце (ответ ещё печатается) молча
 * пропускаем: показать человеку сырой JSON хуже, чем показать график на
 * полсекунды позже.
 */
export function splitAnswer(text: string): Block[] {
  const out: Block[] = [];
  let rest = text;

  for (;;) {
    const at = rest.indexOf(FENCE);
    if (at === -1) break;

    const before = rest.slice(0, at);
    if (before.trim()) out.push({ kind: "text", text: before.trim() });

    const body = rest.slice(at + FENCE.length);
    const close = body.indexOf("```");
    // Блок ещё не дописан — остальное придёт следующим кадром потока.
    if (close === -1) return out;

    const chart = parseChart(body.slice(0, close));
    if (chart) out.push({ kind: "chart", chart });
    rest = body.slice(close + 3);
  }

  if (rest.trim()) out.push({ kind: "text", text: rest.trim() });
  return out;
}

/** Разбор тела блока. Любая кривизна — молча ничего: график не настолько важен,
 *  чтобы ронять из-за него весь ответ. */
function parseChart(raw: string): Chart | null {
  let data: unknown;
  try {
    data = JSON.parse(raw.trim());
  } catch {
    return null;
  }
  if (typeof data !== "object" || data === null) return null;

  const { title, rows } = data as { title?: unknown; rows?: unknown };
  if (!Array.isArray(rows)) return null;

  const clean: [string, string][] = [];
  for (const row of rows.slice(0, MAX_ROWS)) {
    if (!Array.isArray(row) || row.length < 2) continue;
    const [label, value] = row;
    if (typeof label !== "string") continue;
    // Число принимаем и числом: требовать от модели всегда строку — лишний
    // повод ошибиться, а привести к строке дешевле, чем потерять график.
    const asText = typeof value === "number" ? String(value) : value;
    if (typeof asText !== "string" || !Number.isFinite(Number(asText))) continue;
    clean.push([label, asText]);
  }
  if (clean.length === 0) return null;

  return { title: typeof title === "string" ? title : "", rows: clean };
}
